import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useChangePassword, useUpdateProfile } from '@/features/auth/hooks/useProfile'
import { geolocateApi } from '@/features/geolocate/api/geolocateApi'
import {
    Avatar,
    Box,
    Card,
    Dialog,
    Fieldset,
    Grid,
    Heading,
    HStack,
    Stack,
    Text,
    VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FiKey, FiSave, FiUpload, FiX } from 'react-icons/fi'

/**
 * Profile Page
 * User profile management with settings
 */
export function Profile() {
  const { t } = useTranslation()
  const { user, isLoading: isAuthLoading } = useAuth()
  const updateProfileMutation = useUpdateProfile()
  const changePasswordMutation = useChangePassword()
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [avatarFile, setAvatarFile] = useState(null)
  const [serverErrors, setServerErrors] = useState({})
  const [passwordServerErrors, setPasswordServerErrors] = useState({})

  // Profile form
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    values: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      viber: user?.viber || '',
      agency: user?.agency || '',
      agency_address: user?.agency_address || '',
      agency_site: user?.agency_site || '',
      city: user?.city && typeof user.city === 'object' ? user.city : null,
    },
  })

  // Load city data if user.city is an object with id and name
  useEffect(() => {
    if (user?.city && typeof user.city === 'object' && user.city.id && user.city.name) {
      setValue('city', user.city, { shouldDirty: false })
    } else if (user?.city && typeof user.city === 'number') {
      // City is just an ID - can't display without fetching
      // Leave as null, user will need to select city again
      setValue('city', null, { shouldDirty: false })
    }
  }, [user?.city, setValue])

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    watch,
    formState: { errors: passwordErrors },
  } = useForm({
    defaultValues: {
      old_password: '',
      new_password: '',
      new_password_confirm: '',
    },
  })

  const newPassword = watch('new_password')

  const onSubmit = async (data) => {
    try {
      setServerErrors({})
      // Create FormData for multipart/form-data
      const formData = new FormData()

      // Add text fields (excluding empty values)
      Object.keys(data).forEach(key => {
        if (data[key]) {
          // Convert city to integer if present
          if (key === 'city') {
            // AutocompleteSelect returns full city object { id, name, ... }
            const cityId = data[key]?.id || parseInt(data[key], 10)
            if (!isNaN(cityId)) {
              formData.append(key, cityId)
            }
          } else {
            formData.append(key, data[key])
          }
        }
      })

      // Add avatar file if selected
      if (avatarFile) {
        formData.append('image', avatarFile)
      }

      await updateProfileMutation.mutateAsync(formData)
    } catch (error) {
      // Show field-level errors under inputs
      if (error.error?.details) {
        setServerErrors(error.error.details)
      }
    }
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setAvatarFile(file)
    }
  }

  const onPasswordSubmit = async (data) => {
    try {
      setPasswordServerErrors({})
      await changePasswordMutation.mutateAsync({
        old_password: data.old_password,
        new_password: data.new_password,
        new_password_confirm: data.new_password_confirm,
      })

      resetPassword()
      setIsPasswordModalOpen(false)
    } catch (error) {
      // Show field-level errors under inputs
      if (error.error?.details) {
        setPasswordServerErrors(error.error.details)
      }
    }
  }

  if (isAuthLoading) {
    return <Spinner fullScreen />
  }

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" color="#172B4D" mb={1}>
            {t('profile.title')}
          </Heading>
          <Text color="#5E6C84" fontSize="14px">
            {t('profile.personalInfo')}
          </Text>
        </Box>

        <Grid
          templateColumns={{ base: '1fr', lg: '300px 1fr' }}
          gap={6}
        >
          {/* Left Column - Avatar & Password */}
          <VStack spacing={4} align="stretch">
            {/* Avatar Card */}
            <Card.Root
              bg="white"
              borderRadius="8px"
              boxShadow="0 1px 2px rgba(0,0,0,0.08)"
              border="1px solid #DFE1E6"
            >
              <Card.Body p={6}>
                <VStack spacing={4}>
                  <Avatar.Root size="2xl">
                    <Avatar.Image src={user?.avatar} alt={user?.username} />
                    <Avatar.Fallback bg="#0052CC" color="white" fontSize="3xl" fontWeight="600">
                      {(user?.first_name || user?.username || '?')[0].toUpperCase()}
                    </Avatar.Fallback>
                  </Avatar.Root>

                  <VStack spacing={1}>
                    <Text fontSize="lg" fontWeight="600" color="#172B4D">
                      {user?.first_name} {user?.last_name}
                    </Text>
                    <Text fontSize="sm" color="#5E6C84">
                      @{user?.username}
                    </Text>
                  </VStack>

                  <Button
                    variant="outline"
                    size="sm"
                    width="full"
                    borderColor="#DFE1E6"
                    color="#42526E"
                    _hover={{ bg: '#F4F5F7', borderColor: '#B3BAC5' }}
                  >
                    <HStack spacing={2}>
                      <Box as={FiUpload} />
                      <span>Upload Avatar</span>
                    </HStack>
                  </Button>
                </VStack>
              </Card.Body>
            </Card.Root>

            {/* Password Change Button */}
            <Button
              variant="outline"
              borderColor="#DFE1E6"
              color="#42526E"
              _hover={{ bg: '#F4F5F7', borderColor: '#B3BAC5' }}
              onClick={() => {
                setPasswordServerErrors({})
                setIsPasswordModalOpen(true)
              }}
              px={4}
              py={2}
              height="auto"
            >
              <HStack spacing={2}>
                <Box as={FiKey} />
                <span>{t('profile.changePasswordButton')}</span>
              </HStack>
            </Button>
          </VStack>

          {/* Right Column - Form */}
          <Card.Root
            bg="white"
            borderRadius="8px"
            boxShadow="0 1px 2px rgba(0,0,0,0.08)"
            border="1px solid #DFE1E6"
          >
            <Card.Body p={6}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={4}>
                  {/* First Name */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.firstName')}
                    </Fieldset.Legend>
                    <Input
                      {...register('first_name')}
                      placeholder={t('profile.firstName')}
                    />
                    {serverErrors.first_name && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {serverErrors.first_name[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* Last Name */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.lastName')}
                    </Fieldset.Legend>
                    <Input
                      {...register('last_name')}
                      placeholder={t('profile.lastName')}
                    />
                    {serverErrors.last_name && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {serverErrors.last_name[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* Email */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('auth.email')}
                    </Fieldset.Legend>
                    <Input
                      {...register('email')}
                      placeholder={t('auth.email')}
                      type="email"
                    />
                    {serverErrors.email && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {serverErrors.email[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* Phone */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.phone')}
                    </Fieldset.Legend>
                    <Input
                      {...register('phone')}
                      placeholder="+380"
                      type="tel"
                    />
                    {serverErrors.phone && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {serverErrors.phone[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* Viber */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      Viber
                    </Fieldset.Legend>
                    <Input
                      {...register('viber')}
                      placeholder="+380"
                      type="tel"
                    />
                    {serverErrors.viber && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {serverErrors.viber[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* Agency */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.agency')}
                    </Fieldset.Legend>
                    <Input
                      {...register('agency')}
                      placeholder={t('profile.agency')}
                    />
                    {serverErrors.agency && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {serverErrors.agency[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* Agency Address */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.agencyAddress')}
                    </Fieldset.Legend>
                    <Input
                      {...register('agency_address')}
                      placeholder="вул. Хрещатик, 1, Київ"
                    />
                    {serverErrors.agency_address && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {serverErrors.agency_address[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* Agency Site */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.agencySite')}
                    </Fieldset.Legend>
                    <Input
                      {...register('agency_site')}
                      placeholder="https://example.com"
                      type="url"
                    />
                    {serverErrors.agency_site && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {serverErrors.agency_site[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* City */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.city')}
                    </Fieldset.Legend>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <AutocompleteSelect
                          {...field}
                          placeholder={t('profile.cityPlaceholder')}
                          fetchOptions={geolocateApi.getCitiesAutocomplete}
                          displayField={(city) => city.name}
                          noOptionsMessage={t('profile.noCitiesFound')}
                        />
                      )}
                    />
                    {serverErrors.city ? (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {serverErrors.city[0]}
                      </Text>
                    ) : (
                      <Text fontSize="xs" color="#5E6C84" mt={1}>
                        {t('profile.cityHint')}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* Avatar Upload */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.avatar')}
                    </Fieldset.Legend>
                    <Input
                      type="file"
                      accept="image/*"
                      size="sm"
                      onChange={handleAvatarChange}
                    />
                    {avatarFile && (
                      <Text fontSize="xs" color="#5E6C84" mt={1}>
                        Обрано: {avatarFile.name}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* Submit Button */}
                  <Box pt={4}>
                    <Button
                      type="submit"
                      variant="solid"
                      bg="#00B8A9"
                      color="white"
                      _hover={{ bg: '#009688' }}
                      _active={{ bg: '#00796B' }}
                      disabled={!isDirty || updateProfileMutation.isPending}
                      width="full"
                      px={4}
                      py={3}
                      height="auto"
                      fontSize="14px"
                      fontWeight="600"
                    >
                      <HStack spacing={2}>
                        <Box as={FiSave} />
                        <span>{t('profile.saveChanges')}</span>
                      </HStack>
                    </Button>
                  </Box>
                </Stack>
              </form>
            </Card.Body>
          </Card.Root>
        </Grid>
      </VStack>

      {/* Password Change Modal */}
      <Dialog.Root
        open={isPasswordModalOpen}
        onOpenChange={(e) => {
          setIsPasswordModalOpen(e.open)
          if (!e.open) {
            // Clear errors when closing modal
            setPasswordServerErrors({})
            resetPassword()
          }
        }}
        placement="center"
      >
        <Dialog.Backdrop bg="rgba(9, 30, 66, 0.54)" />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="500px"
            bg="white"
            borderRadius="3px"
            boxShadow="0 8px 16px 0 rgba(9, 30, 66, 0.16), 0 0 1px 0 rgba(9, 30, 66, 0.24)"
            p={0}
          >
            <Dialog.Header
              fontSize="20px"
              fontWeight="500"
              color="#172B4D"
              p={6}
              pb={3}
              borderBottom="1px solid #DFE1E6"
            >
              <Dialog.Title>{t('profile.changePassword')}</Dialog.Title>
            </Dialog.Header>

            <Dialog.CloseTrigger asChild>
              <Box
                as="button"
                type="button"
                position="absolute"
                top="12px"
                right="12px"
                w="32px"
                h="32px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="3px"
                color="#42526E"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ bg: '#F4F5F7' }}
                aria-label="Close"
              >
                <FiX size="20px" />
              </Box>
            </Dialog.CloseTrigger>

            <Dialog.Body px={6} py={6}>
              <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                <Stack spacing={4}>
                  {/* Non-field errors */}
                  {passwordServerErrors.non_field_errors && (
                    <Box
                      bg="#FFEBE6"
                      border="1px solid #FF8B00"
                      borderRadius="3px"
                      p={3}
                    >
                      <Text fontSize="sm" color="#DE350B">
                        {passwordServerErrors.non_field_errors[0]}
                      </Text>
                    </Box>
                  )}

                  {/* Old Password */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.currentPassword')}
                    </Fieldset.Legend>
                    <Input
                      {...registerPassword('old_password', { required: 'Current password is required' })}
                      type="password"
                      placeholder={t('profile.enterCurrentPassword')}
                    />
                    {(passwordErrors.old_password || passwordServerErrors.old_password) && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {passwordErrors.old_password?.message || passwordServerErrors.old_password?.[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* New Password */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.newPassword')}
                    </Fieldset.Legend>
                    <Input
                      {...registerPassword('new_password', {
                        required: 'Password is required',
                        minLength: { value: 8, message: 'Password must be at least 8 characters' }
                      })}
                      type="password"
                      placeholder={t('profile.enterNewPassword')}
                    />
                    {(passwordErrors.new_password || passwordServerErrors.new_password) && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {passwordErrors.new_password?.message || passwordServerErrors.new_password?.[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  {/* Confirm Password */}
                  <Fieldset.Root>
                    <Fieldset.Legend fontSize="sm" fontWeight="500" color="#42526E" mb={2}>
                      {t('profile.confirmPassword')}
                    </Fieldset.Legend>
                    <Input
                      {...registerPassword('new_password_confirm', {
                        required: 'Please confirm your password',
                        validate: (value) => value === newPassword || 'Passwords do not match'
                      })}
                      type="password"
                      placeholder={t('profile.confirmNewPassword')}
                    />
                    {(passwordErrors.new_password_confirm || passwordServerErrors.new_password_confirm) && (
                      <Text fontSize="xs" color="#DE350B" mt={1}>
                        {passwordErrors.new_password_confirm?.message || passwordServerErrors.new_password_confirm?.[0]}
                      </Text>
                    )}
                  </Fieldset.Root>

                  <HStack justify="flex-end" pt={4} spacing={3}>
                    <Button
                      variant="outline"
                      onClick={() => setIsPasswordModalOpen(false)}
                      borderColor="#DFE1E6"
                      color="#42526E"
                      _hover={{ bg: '#F4F5F7' }}
                      px={4}
                      py={2}
                      height="auto"
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="solid"
                      bg="#0052CC"
                      color="white"
                      _hover={{ bg: '#0747A6' }}
                      disabled={changePasswordMutation.isPending}
                      px={4}
                      py={2}
                      height="auto"
                    >
                      {t('profile.changePassword')}
                    </Button>
                  </HStack>
                </Stack>
              </form>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  )
}

export default Profile
